















import Component, { mixins } from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { InputPayeeInformation, PayeeInformation } from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import { PayoutSystemSettings } from "@scrile/api-provider/dist/api/PayoutSystemSettingsProvider";
import WithAuthUser from "@/mixins/WithAuthUser";
import providers from "@/lib/api";
import { isCryptoSystem } from "@/lib/moneyHelpers";
import { FormValues, ListFields } from "@/types";
import BaseInput from "@/components/BaseInput";
import BaseSelect from "@/components/BaseSelect";
import getComponent, { getListFields } from "../integrations";
import { Countries_convert_3to2 } from "../integrations/components/MassPayPayout/masspay_countries";

@Component({ name: "PayoutForm", components: { BaseSelect, BaseInput } })
export default class PayoutForm extends mixins(WithAuthUser) {
  @Prop({ required: false })
  public readonly hideDescription!: boolean;

  public configuredPayoutSystems: PayoutSystemSettings[] = [];

  public payeeInformation: PayeeInformation | null = null;

  public loading: boolean = false;

  public values: FormValues = {
    payoutSystem: "not_configured",
    currency: "not_configured",
    other: null,
    paxum: null,
    paypal: null,
    bank: null,
    bitcoin_p2p: null,
    masspay: null,
  };

  public get hasCryptoSystem() {
    return this.configuredPayoutSystems.some(item => isCryptoSystem(item.systemName));
  }

  public get payoutSystemForm() {
    return getComponent(this.selectedPayoutSystem).component;
  }

  public get selectedPayoutSystem(): string {
    return "masspay";
  }

  public get systemList(): ListFields {
    return getListFields().filter(
      i => !!this.configuredPayoutSystems.find(s => s.systemName === i.value && s.currencies.length > 0)
    );
  }

  public get currencyList() {
    const selectedPayoutSystem = this.configuredPayoutSystems.find(i => i.systemName === this.selectedPayoutSystem);
    return selectedPayoutSystem ? selectedPayoutSystem.currencies.map(i => ({ value: i, text: i })) : [];
  }

  public get bitcoinWalletValue() {
    return this.values.bitcoin_p2p?.address ?? "";
  }

  @Watch("currencyList")
  public onCurrencyList(list: { text: string; value: string }[]) {
    if (!list.find(i => i.value === this.values["currency"])) {
      this.input(list[0]?.value || "0", "currency");
    }
  }

  public input(value: string | null, field: string) {
    this.$set(this.values, field, value);
  }

  public bitcoinWalletInput(value: string) {
    this.$set(this.values, "bitcoin_p2p", { address: value });
  }

  public systemInfoInput(values: FormValues) {
    this.values = { ...this.values, ...values };
  }

  public async submitPayeeInfo() {
    const data: InputPayeeInformation = {
      userId: this.authUserStrict.id,
      payoutSystem: this.values.payoutSystem || "not_configured",
      currency: this.values.currency || "not_configured",
      country: Countries_convert_3to2[this.values.country as keyof typeof Countries_convert_3to2] || "",
      bitcoin_p2p: { address: this.bitcoinWalletValue },
      ...((this.values.payoutSystem !== "not_configured" && {
        [this.values.payoutSystem]: this.values[this.values.payoutSystem],
      }) ||
        {}),
    };
    this.payeeInformation = await providers.PayeeInformationProvider.update({ data });
  }

  public async getData() {
    this.loading = true;
    await Promise.all([
      providers.PayoutSystemSettingsProvider.findAll().then(r => (this.configuredPayoutSystems = r)),
      providers.PayeeInformationProvider.find({ id: this.authUserStrict.id })
        .then(r => {
          this.payeeInformation = r;
          this.values = {
            ...this.values,
            payoutSystem: this.payeeInformation?.payoutSystem ?? "not_configured",
            currency: this.payeeInformation?.currency ?? "not_configured",
            country: this.payeeInformation?.country ?? "",
            other: this.payeeInformation?.other,
            paxum: this.payeeInformation?.paxum,
            paypal: this.payeeInformation?.paypal,
            bank: this.payeeInformation?.bank,
            bitcoin_p2p: this.payeeInformation?.bitcoin_p2p,
            masspay: this.payeeInformation?.masspay,
          };
        })
        .finally(() => (this.loading = false)),
    ]);
  }

  public async created() {
    this.$emit("setValid", true);
    this.$emit("setCb", () => this.submitPayeeInfo());
    try {
      this.$emit("setLoading", true);
      await this.getData();
    } finally {
      this.$emit("setLoading", false);
    }
  }
}
